import { Controller, useFormContext } from "react-hook-form";
import { AddOnTextInput, ErrorMessage, Icon, TextInput, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useCountryConfig } from "@/hooks";
import { getCurrencySymbol } from "@/utils";
import { VehicleTypeFormData } from "./form";

const Label = ({ children, info }: { children: React.ReactNode; info?: string }) => (
  <Typography className="flex items-center gap-1.5 text-base font-semibold leading-5 text-neutral-dark-gray">
    {children}
    {info && (
      <Tooltip content={info} placement="top">
        <span className="inline-flex">
          <Icon name="InfoCircle" size="sm" className="text-neutral-silver-gray" />
        </span>
      </Tooltip>
    )}
  </Typography>
);

export const StepOneFields = () => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<VehicleTypeFormData>();

  const isCustomCommissionEnabled = watch("customCommissionEnabled");

  const { config } = useCountryConfig();
  /**
   * "GBP £" / "AUD $" — code and symbol, matching how the pricing and cancellation screens label an
   * amount. Both halves drop out when the operator's country is unknown, so the field renders with
   * no prefix rather than "undefined"; deliberately not defaulting to AUD, per countryConfig's
   * NEUTRAL_COUNTRY_CONFIG.
   */
  const currencyPrefix = [config.currency, getCurrencySymbol(config.currency)].filter(Boolean).join(" ");

  return (
    <div className="flex flex-col gap-6">
      {/* Name */}
      <div className="flex flex-col gap-1">
        <Label>Name of Vehicle Type</Label>
        <TextInput
          className="w-full"
          placeholder="Enter Name of Vehicle Type"
          maxLength={32}
          hasError={!!errors.name}
          {...register("name")}
        />
        <Typography className="text-base leading-5 text-neutral-silver-gray">This is what your customers would see at booking</Typography>
        <ErrorMessage errors={errors} name="name" />
      </div>

      {/* Capacity + Bags */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <Label>Capacity</Label>
          <TextInput
            type="number"
            className="w-full"
            placeholder="0"
            min={0}
            max={999}
            step={1}
            hasError={!!errors.capacity}
            {...register("capacity", { valueAsNumber: true })}
          />
          <ErrorMessage errors={errors} name="capacity" />
        </div>
        <div className="flex flex-col gap-1">
          <Label>No. of Bags</Label>
          <TextInput
            type="number"
            className="w-full"
            placeholder="0"
            min={0}
            max={999}
            step={1}
            hasError={!!errors.bags}
            {...register("bags", { valueAsNumber: true })}
          />
          <ErrorMessage errors={errors} name="bags" />
        </div>
      </div>

      {/* Waiting Time + Cost Adjustment */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <Label>Waiting Time /minute</Label>
          <AddOnTextInput
            className="[&>div]:w-full"
            type="number"
            placeholder="0"
            step="0.01"
            startAddOn={currencyPrefix}
            hasError={!!errors.waitTimeCost}
            {...register("waitTimeCost", { valueAsNumber: true })}
          />
          <ErrorMessage errors={errors} name="waitTimeCost" />
        </div>
        <div className="flex flex-col gap-1">
          <Label info="The base trip cost will increase by the percentage you've entered">Cost Adjustment</Label>
          <AddOnTextInput
            className="[&>div]:w-full [&_input]:text-right"
            type="number"
            placeholder="0"
            endAddOn="%"
            step="1"
            hasError={!!errors.costAdjustment}
            {...register("costAdjustment", { valueAsNumber: true })}
          />
          <ErrorMessage errors={errors} name="costAdjustment" />
        </div>
      </div>

      {/* Custom Driver Commission */}
      <div className="flex items-center justify-between gap-4">
        <Controller
          name="customCommissionEnabled"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-3">
              <Toggle
                checked={field.value}
                onChange={(checked) => {
                  field.onChange(checked);
                  if (!checked) setValue("customCommissionRate", 0, { shouldDirty: true });
                }}
              />
              <Label info="When active, this will be the default driver payment when dispatching jobs">Custom Driver Commission</Label>
            </div>
          )}
        />
        {isCustomCommissionEnabled && (
          <div className="flex flex-col gap-1">
            <AddOnTextInput
              className="[&_input]:w-[63px] [&_input]:text-right"
              type="number"
              endAddOn="%"
              placeholder="0"
              step="1"
              min={0}
              max={100}
              hasError={!!errors.customCommissionRate}
              {...register("customCommissionRate", { valueAsNumber: true })}
            />
            <ErrorMessage errors={errors} name="customCommissionRate" />
          </div>
        )}
      </div>
    </div>
  );
};
